import LegalDocument, {
  type LegalDocumentSection,
} from "@/components/LegalDocument";
import {
  SEO_DEFAULT_ORIGIN,
  SEO_DEFAULT_SOCIAL_IMAGE,
  type SeoMetadata,
} from "@shared/seo";

const SUPPORT_EMAIL = "support@techfare.com";
const EFFECTIVE_DATE = "March 21, 2026";

const PRIVACY_SECTIONS: LegalDocumentSection[] = [
  {
    id: "privacy",
    title: "Privacy",
    summary:
      "techfare® is committed to protecting the privacy and security of its users. This privacy policy explains the types of data we collect, how we use that data, and how we protect user privacy.",
    paragraphs: [
      "techfare® is committed to protecting the privacy and security of its users. This privacy policy explains the types of data we collect, how we use that data, and how we protect user privacy.",
    ],
  },
  {
    id: "data-collection",
    title: "Data Collection",
    summary:
      "We may collect personal information such as name, email address, phone number, postal address from users of our website.",
    paragraphs: [
      "We may collect personal information such as name, email address, phone number, postal address from users of our website. We may also collect non-personal information such as browser type, IP address, and clickstream data. We may collect data from third-party sources, such as social media platforms, advertising networks, and data brokers.",
    ],
  },
  {
    id: "data-usage",
    title: "Data Usage",
    summary:
      "We use the data we collect to provide and improve our services, to communicate with users, and to personalize the user experience.",
    paragraphs: [
      "We use the data we collect to provide and improve our services, to communicate with users, and to personalize the user experience. We may use personal information to process payments, provide customer service, and respond to inquiries. We may use non-personal information for analytics purposes, to improve our website and services, and for marketing and advertising purposes. We may share data with third parties in certain circumstances, such as to process payments or to comply with legal obligations. We may also share data with service providers who help us operate our website and provide our services, such as hosting providers, payment processors, and customer service providers. We require these service providers to protect user data and comply with relevant laws and regulations.",
    ],
  },
  {
    id: "paypal-auth",
    title: "PayPal Authentication for Payouts",
    summary:
      "For sellers on our platform, we use \"Log in with PayPal\" to securely link their PayPal accounts for payouts.",
    paragraphs: [
      "For sellers on our platform, we use \"Log in with PayPal\" to securely link their PayPal accounts for payouts. This process ensures that payouts are sent to verified PayPal accounts, reducing errors and enhancing the security of financial transactions. It is important to note that this feature is solely for linking PayPal accounts for payouts and is not a method for signing into the techfare platform. By authenticating through PayPal, sellers authorize us to use their PayPal account for processing payments related to their sales.",
    ],
  },
  {
    id: "stripe-payouts",
    title: "Stripe Payouts and Card Payments",
    summary:
      "Sellers who connect Stripe share their account details with Stripe, and buyers who pay by card share their payment details with Stripe.",
    paragraphs: [
      "Sellers can connect a Stripe account for payouts. Stripe collects the identity, business and bank details it needs to verify the seller directly, under Stripe's own privacy policy; techfare stores only the Stripe account ID and whether the account can accept payments. When a buyer pays by card, Apple Pay, Google Pay or Link, the payment details are entered on Stripe's checkout page and handled by Stripe. techfare receives the order details it needs to complete the sale, such as the amount, the payment status and the shipping address.",
    ],
  },
  {
    id: "data-protection",
    title: "Data Protection",
    summary:
      "We take appropriate technical and organizational measures to protect user data, including encryption, firewalls, and access controls.",
    paragraphs: [
      "We take appropriate technical and organizational measures to protect user data, including encryption, firewalls, and access controls. We limit access to user data to authorized personnel who require access to perform their duties. In the event of a data breach, we will notify affected users as required by law.",
    ],
  },
  {
    id: "user-rights",
    title: "User Rights",
    summary:
      "Users have the right to access, correct, and delete their data. Users may also request that we stop using their data or that we delete their data altogether.",
    paragraphs: [
      "Users have the right to access, correct, and delete their data. Users may also request that we stop using their data or that we delete their data altogether. We will comply with all reasonable requests, subject to legal obligations. Users can exercise their rights by contacting us. We may require users to provide certain information to verify their identity before we can fulfill their request.",
    ],
  },
  {
    id: "cookies-tracking",
    title: "Cookies and Tracking",
    summary:
      "We use cookies and other tracking technologies to collect information about users' browsing behavior and to personalize the user experience.",
    paragraphs: [
      "We use cookies and other tracking technologies to collect information about users' browsing behavior and to personalize the user experience. Cookies are small files that are stored on a user's device and are used to remember user preferences and track website usage. We use both session cookies, which expire when the user closes their browser, and persistent cookies, which remain on the user's device until they expire or are deleted. Users can control cookie settings in their browser and may choose to disable cookies altogether. However, disabling cookies may affect the functionality of our website. We may also use other tracking technologies, such as web beacons and pixel tags, to collect information about user behavior and website usage.",
    ],
  },
  {
    id: "third-party-information",
    title: "Personal Information from Third Parties",
    summary:
      "In addition to the information collected directly from you through our Services, we also collect Personal Information about you from other sources and third parties.",
    paragraphs: [
      "In addition to the information collected directly from you through our Services, we also collect Personal Information about you from other sources and third parties. This information may be added to the data we have already collected from you. The sources of this third-party information include:",
    ],
    bullets: [
      "Social Networks: We may collect Personal Information about you when you sign up or log in to our Services via third-party platforms such as Google, Facebook, Apple, or Twitter. This information may include your social media user ID and email address. Additionally, if you post photos, comments, or reviews on our pages available through third-party platforms or share products from our Services through these networks, the Personal Information you provide may be viewable by us and other users on those platforms.",
      "Data Partners: We may receive additional Personal Information about you, such as approximate geographic location, demographic information, and mail order responses for categories of interest, from third parties. These third parties provide information about you based on your IP address, unique online identifier, or other data points you have provided to us.",
    ],
  },
  {
    id: "policy-changes",
    title: "Changes to the Privacy Policy",
    summary:
      "We may update this privacy policy from time to time. We will notify users of any material changes and provide a date of last update.",
    paragraphs: [
      "We may update this privacy policy from time to time. We will notify users of any material changes and provide a date of last update.",
    ],
  },
  {
    id: "contact",
    title: "Contact Information",
    summary:
      "If you have any questions or concerns about our privacy policy, feel free to reach out to us. We're here to help and provide the information you need.",
    paragraphs: [
      "If you have any questions or concerns about our privacy policy, feel free to reach out to us. We're here to help and provide the information you need. Your privacy is important to us, and we want to make sure you're comfortable with our policies. Don't hesitate to contact us.",
    ],
  },
];

function buildPrivacyMetadata(): SeoMetadata {
  const origin =
    typeof window === "undefined"
      ? SEO_DEFAULT_ORIGIN
      : window.location.origin.replace(/\/+$/, "");

  return {
    title: "Privacy Policy | Techfare",
    description:
      "Read Techfare Privacy Policy, including how we collect, use, protect, and share data.",
    canonicalUrl: `${origin}/privacy-policy`,
    robots:
      "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    ogType: "website",
    ogImage: SEO_DEFAULT_SOCIAL_IMAGE,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Techfare Privacy Policy",
        description:
          "Techfare privacy disclosures covering collection, use, protection, and user rights.",
        url: `${origin}/privacy-policy`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${origin}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Privacy Policy",
            item: `${origin}/privacy-policy`,
          },
        ],
      },
    ],
  };
}

export default function PrivacyPolicy() {
  return (
    <LegalDocument
      metadata={buildPrivacyMetadata()}
      badge="Privacy Policy"
      title="Privacy"
      intro="techfare® is committed to protecting the privacy and security of its users. This privacy policy explains the types of data we collect, how we use that data, and how we protect user privacy."
      effectiveDate={EFFECTIVE_DATE}
      lastUpdated={EFFECTIVE_DATE}
      sections={PRIVACY_SECTIONS}
      contactEmail={SUPPORT_EMAIL}
      contactLabel={SUPPORT_EMAIL}
      relatedLinks={[
        {
          label: "Terms of Service",
          href: "/terms-of-service",
        },
      ]}
    />
  );
}
